import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Package, ShoppingCart, Truck, Layers, ArrowLeftRight, Undo2, Users, Calendar as CalIcon, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Product360Dialog
 * Vista 360° de un producto: ventas, compras, lotes, movimientos, devoluciones, top clientes/proveedores.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: fn
 *  - productoId: string
 *  - productoNombre: string (display fallback while loading)
 */
export function Product360Dialog({ open, onOpenChange, productoId, productoNombre }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Rango por defecto: últimos 90 días
  const today = new Date().toISOString().slice(0, 10);
  const ninetyAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const [desde, setDesde] = useState(ninetyAgo);
  const [hasta, setHasta] = useState(today);

  const load = async () => {
    if (!productoId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/inventory/productos/${productoId}/kardex360`, {
        params: { desde, hasta },
      });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && productoId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productoId]);

  const verMontos = !!data?.ver_montos;
  const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
  const num = (v) => (v == null ? "—" : Number(v).toLocaleString("es-EC"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto p-0"
        data-testid="product360-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#d1dbd6] sticky top-0 bg-white z-10">
          <DialogTitle className="flex items-center gap-2 text-[#15241d]">
            <Package size={20} className="text-[#205c45]" />
            <span>Vista 360° del producto</span>
            <Badge className="ml-1 bg-[#e6f0eb] text-[#205c45] border border-[#bfd4c9]" data-testid="product360-name">
              {data?.producto?.nombre_comercial || productoNombre || "—"}
            </Badge>
            {!verMontos && (
              <Badge variant="outline" className="ml-2 text-[11px] text-[#5c7368] border-[#d1dbd6]">
                <EyeOff size={11} className="mr-1" />
                Montos ocultos
              </Badge>
            )}
          </DialogTitle>
          <FilterBar desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} onApply={load} loading={loading} />
        </DialogHeader>

        <div className="px-6 py-5">
          {loading && !data ? (
            <div className="flex items-center justify-center py-20 text-[#5c7368]">
              <Loader2 className="animate-spin mr-2" size={18} /> Cargando…
            </div>
          ) : data ? (
            <Product360Body data={data} verMontos={verMontos} money={money} num={num} />
          ) : (
            <div className="text-center py-12 text-[#8ba397]">Sin datos</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FilterBar({ desde, hasta, setDesde, setHasta, onApply, loading }) {
  const setQuick = (days) => {
    const h = new Date().toISOString().slice(0, 10);
    const d = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
    setDesde(d); setHasta(h);
  };
  return (
    <div className="flex flex-wrap items-end gap-2 pt-2">
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Desde</Label>
        <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-8 w-[150px]" data-testid="product360-desde" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Hasta</Label>
        <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-8 w-[150px]" data-testid="product360-hasta" />
      </div>
      <Button size="sm" onClick={onApply} disabled={loading} className="h-8 bg-[#205c45] hover:bg-[#1a4a37]" data-testid="product360-apply">
        {loading ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
        Aplicar
      </Button>
      <div className="flex gap-1 ml-1">
        <Button size="sm" variant="outline" className="h-8" onClick={() => setQuick(30)}>30d</Button>
        <Button size="sm" variant="outline" className="h-8" onClick={() => setQuick(90)}>90d</Button>
        <Button size="sm" variant="outline" className="h-8" onClick={() => setQuick(365)}>1 año</Button>
      </div>
    </div>
  );
}

function Product360Body({ data, verMontos, money, num }) {
  const r = data.resumen || {};
  return (
    <div className="space-y-5">
      <KpiGrid resumen={r} verMontos={verMontos} money={money} num={num} />

      <Tabs defaultValue="resumen" className="w-full">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="resumen" data-testid="p360-tab-resumen"><Package size={13} className="mr-1" /> Resumen</TabsTrigger>
          <TabsTrigger value="ventas" data-testid="p360-tab-ventas"><ShoppingCart size={13} className="mr-1" /> Ventas ({data.ventas?.length || 0})</TabsTrigger>
          <TabsTrigger value="compras" data-testid="p360-tab-compras"><Truck size={13} className="mr-1" /> Compras ({data.compras?.length || 0})</TabsTrigger>
          <TabsTrigger value="lotes" data-testid="p360-tab-lotes"><Layers size={13} className="mr-1" /> Lotes ({data.lotes?.length || 0})</TabsTrigger>
          <TabsTrigger value="movs" data-testid="p360-tab-movs"><ArrowLeftRight size={13} className="mr-1" /> Movimientos ({data.movimientos?.length || 0})</TabsTrigger>
          <TabsTrigger value="devs" data-testid="p360-tab-devs"><Undo2 size={13} className="mr-1" /> Devoluciones ({(data.devoluciones_cliente?.length || 0) + (data.devoluciones_proveedor?.length || 0)})</TabsTrigger>
        </TabsList>

        <TabsContent value="resumen">
          <ResumenTab data={data} verMontos={verMontos} money={money} num={num} />
        </TabsContent>
        <TabsContent value="ventas">
          <VentasTable rows={data.ventas} verMontos={verMontos} money={money} num={num} />
        </TabsContent>
        <TabsContent value="compras">
          <ComprasTable rows={data.compras} verMontos={verMontos} money={money} num={num} />
        </TabsContent>
        <TabsContent value="lotes">
          <LotesTable rows={data.lotes} verMontos={verMontos} money={money} num={num} />
        </TabsContent>
        <TabsContent value="movs">
          <MovsTable rows={data.movimientos} verMontos={verMontos} money={money} num={num} />
        </TabsContent>
        <TabsContent value="devs">
          <DevolucionesTabs data={data} verMontos={verMontos} money={money} num={num} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KpiCard({ label, value, sub, testId }) {
  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">{label}</div>
      <div className="text-2xl font-bold text-[#15241d] tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-[#5c7368] mt-0.5">{sub}</div>}
    </div>
  );
}

function KpiGrid({ resumen, verMontos, money, num }) {
  const r = resumen || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
      <KpiCard label="Stock total" value={num(r.stock_total)} sub={`${r.lotes_activos || 0} lote(s)`} testId="kpi-stock" />
      <KpiCard
        label="Vendido en el período"
        value={num(r.ventas_cantidad)}
        sub={verMontos ? money(r.ventas_monto) : "u."}
        testId="kpi-ventas"
      />
      <KpiCard
        label="Comprado en el período"
        value={num(r.compras_cantidad)}
        sub={verMontos ? money(r.compras_monto) : "u."}
        testId="kpi-compras"
      />
      <KpiCard label="Rotación diaria" value={`${num(r.rotacion_diaria)}/d`} sub={`${r.periodo_dias || 0} días`} testId="kpi-rotacion" />
      <KpiCard label="Días de inventario" value={r.dias_inventario_estimados != null ? `${r.dias_inventario_estimados}d` : "—"} sub="estimados al ritmo actual" testId="kpi-dias-inv" />
      <KpiCard
        label="Devoluciones (cant.)"
        value={`${num(r.devoluciones_cliente_cantidad)} / ${num(r.devoluciones_proveedor_cantidad)}`}
        sub="cliente / proveedor"
        testId="kpi-devs"
      />
    </div>
  );
}

function ResumenTab({ data, verMontos, money, num }) {
  return (
    <div className="grid md:grid-cols-2 gap-4 mt-4">
      <div className="rounded-md border border-[#d1dbd6] bg-white">
        <div className="px-4 py-2.5 border-b border-[#d1dbd6] flex items-center gap-2 text-sm font-semibold text-[#15241d]">
          <Users size={14} /> Top clientes
        </div>
        <div className="overflow-x-auto">
          <table className="erp-table" data-testid="top-clientes-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th className="text-right">Cantidad</th>
                {verMontos && <th className="text-right">Monto</th>}
                <th className="text-right">Facturas</th>
                <th>Última</th>
              </tr>
            </thead>
            <tbody>
              {(data.top_clientes || []).map((c, i) => (
                <tr key={i}>
                  <td className="font-medium">{c.cliente_nombre}</td>
                  <td className="text-right tabular-nums">{num(c.cantidad)}</td>
                  {verMontos && <td className="text-right tabular-nums">{money(c.monto)}</td>}
                  <td className="text-right tabular-nums">{c.facturas}</td>
                  <td className="text-[#4a5c53]">{c.ultima_fecha || "—"}</td>
                </tr>
              ))}
              {(!data.top_clientes || data.top_clientes.length === 0) && (
                <tr><td colSpan={verMontos ? 5 : 4} className="text-center py-6 text-[#8ba397]">Sin ventas en el período</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white">
        <div className="px-4 py-2.5 border-b border-[#d1dbd6] flex items-center gap-2 text-sm font-semibold text-[#15241d]">
          <Truck size={14} /> Top proveedores
        </div>
        <div className="overflow-x-auto">
          <table className="erp-table" data-testid="top-proveedores-table">
            <thead>
              <tr>
                <th>Proveedor</th>
                <th className="text-right">Cantidad</th>
                {verMontos && <th className="text-right">Monto</th>}
                <th className="text-right">Facturas</th>
                <th>Última</th>
              </tr>
            </thead>
            <tbody>
              {(data.top_proveedores || []).map((p, i) => (
                <tr key={i}>
                  <td className="font-medium">{p.proveedor_nombre}</td>
                  <td className="text-right tabular-nums">{num(p.cantidad)}</td>
                  {verMontos && <td className="text-right tabular-nums">{money(p.monto)}</td>}
                  <td className="text-right tabular-nums">{p.facturas}</td>
                  <td className="text-[#4a5c53]">{p.ultima_fecha || "—"}</td>
                </tr>
              ))}
              {(!data.top_proveedores || data.top_proveedores.length === 0) && (
                <tr><td colSpan={verMontos ? 5 : 4} className="text-center py-6 text-[#8ba397]">Sin compras en el período</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===== Pagination helper inline (20 por página) =====
function usePaged(rows) {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const total = (rows || []).length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { setPage(1); }, [rows]);
  const slice = useMemo(() => (rows || []).slice((page - 1) * pageSize, page * pageSize), [rows, page]);
  return { page, setPage, pages, total, slice };
}

function Pager({ page, setPage, pages, total }) {
  if (total <= 20) return null;
  return (
    <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-[#d1dbd6] bg-[#f7f9f8] text-xs">
      <span className="text-[#5c7368]">Página {page} de {pages} ({total})</span>
      <Button size="sm" variant="outline" className="h-7" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
      <Button size="sm" variant="outline" className="h-7" disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
    </div>
  );
}

function VentasTable({ rows, verMontos, money, num }) {
  const { page, setPage, pages, total, slice } = usePaged(rows);
  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden mt-4" data-testid="ventas-table">
      <table className="erp-table">
        <thead>
          <tr>
            <th>Fecha</th><th>Factura</th><th>Cliente</th><th className="text-right">Cant.</th>
            {verMontos && <th className="text-right">P. unit</th>}
            {verMontos && <th className="text-right">Total</th>}
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {slice.map((r, i) => (
            <tr key={i}>
              <td className="tabular-nums">{r.fecha}</td>
              <td className="font-mono text-xs">{r.numero}</td>
              <td className="font-medium">{r.cliente_nombre || "—"}</td>
              <td className="text-right tabular-nums">{num(r.cantidad)}</td>
              {verMontos && <td className="text-right tabular-nums">{money(r.precio_unitario)}</td>}
              {verMontos && <td className="text-right tabular-nums">{money(r.total)}</td>}
              <td><Badge variant="outline" className="text-[10px]">{r.estado || "—"}</Badge></td>
            </tr>
          ))}
          {total === 0 && <tr><td colSpan={verMontos ? 7 : 5} className="text-center py-8 text-[#8ba397]">Sin ventas en el período</td></tr>}
        </tbody>
      </table>
      <Pager page={page} setPage={setPage} pages={pages} total={total} />
    </div>
  );
}

function ComprasTable({ rows, verMontos, money, num }) {
  const { page, setPage, pages, total, slice } = usePaged(rows);
  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden mt-4" data-testid="compras-table">
      <table className="erp-table">
        <thead>
          <tr>
            <th>Fecha</th><th>Factura</th><th>Lote</th><th>Proveedor</th><th className="text-right">Cant.</th>
            {verMontos && <th className="text-right">C. unit</th>}
            {verMontos && <th className="text-right">Total</th>}
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {slice.map((r, i) => (
            <tr key={i}>
              <td className="tabular-nums">{r.fecha}</td>
              <td className="font-mono text-xs">{r.numero}</td>
              <td className="font-mono text-xs" data-testid={`compra-lote-${i}`}>{r.numero_lote || "—"}</td>
              <td className="font-medium">{r.proveedor_nombre || "—"}</td>
              <td className="text-right tabular-nums">{num(r.cantidad)}</td>
              {verMontos && <td className="text-right tabular-nums">{money(r.costo_unitario)}</td>}
              {verMontos && <td className="text-right tabular-nums">{money(r.total)}</td>}
              <td><Badge variant="outline" className="text-[10px]">{r.estado || "—"}</Badge></td>
            </tr>
          ))}
          {total === 0 && <tr><td colSpan={verMontos ? 8 : 6} className="text-center py-8 text-[#8ba397]">Sin compras en el período</td></tr>}
        </tbody>
      </table>
      <Pager page={page} setPage={setPage} pages={pages} total={total} />
    </div>
  );
}

function LotesTable({ rows, verMontos, money, num }) {
  const today = new Date().toISOString().slice(0, 10);
  const days = (d) => d ? Math.floor((new Date(d) - new Date(today)) / 86400000) : null;
  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden mt-4" data-testid="lotes-table">
      <table className="erp-table">
        <thead>
          <tr>
            <th>Lote</th><th>Bodega</th><th>Proveedor</th><th>Factura compra</th><th>Elaboración</th><th>Vencimiento</th>
            <th className="text-right">Disponible</th>
            {verMontos && <th className="text-right">C. unit</th>}
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((l, i) => {
            const d = days(l.fecha_vencimiento);
            const vc = d == null ? { cls: "", txt: "—" } :
              d < 0 ? { cls: "fefo-vencido", txt: "Vencido" } :
              d <= 30 ? { cls: "fefo-critico", txt: `${d}d` } :
              { cls: "fefo-safe", txt: `${d}d` };
            return (
              <tr key={i}>
                <td className="font-mono text-xs">{l.numero_lote}</td>
                <td className="text-[#4a5c53]">{l.bodega_id?.slice(0, 8) || "—"}</td>
                <td>{l.proveedor_nombre || "—"}</td>
                <td className="font-mono text-xs" data-testid={`lote-factura-${i}`}>{l.factura_numero || "—"}</td>
                <td className="text-[#4a5c53]">{l.fecha_elaboracion || "—"}</td>
                <td>{l.fecha_vencimiento || "—"}</td>
                <td className="text-right tabular-nums">{num(l.cantidad_disponible)}</td>
                {verMontos && <td className="text-right tabular-nums">{money(l.costo_unitario)}</td>}
                <td><Badge className={`${vc.cls} border text-[10px]`}>{vc.txt}</Badge></td>
              </tr>
            );
          })}
          {(!rows || rows.length === 0) && <tr><td colSpan={verMontos ? 9 : 8} className="text-center py-8 text-[#8ba397]">Sin lotes activos</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function MovsTable({ rows, verMontos, money, num }) {
  const { page, setPage, pages, total, slice } = usePaged(rows);
  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden mt-4" data-testid="movs-table">
      <table className="erp-table">
        <thead>
          <tr>
            <th>Fecha</th><th>Tipo</th><th>Lote</th><th className="text-right">Cantidad</th><th>Documento</th><th>Bodega</th>
          </tr>
        </thead>
        <tbody>
          {slice.map((m, i) => (
            <tr key={i}>
              <td className="tabular-nums">{m.fecha || (m.created_at || "").slice(0, 10)}</td>
              <td><Badge variant="outline" className="text-[10px]">{m.tipo}</Badge></td>
              <td className="font-mono text-xs">{m.numero_lote || "—"}</td>
              <td className="text-right tabular-nums">{num(m.cantidad)}</td>
              <td className="text-[11px] text-[#4a5c53]">{m.documento_ref || "—"}</td>
              <td className="text-[11px] text-[#4a5c53]">{m.bodega_destino_id?.slice(0, 8) || m.bodega_origen_id?.slice(0, 8) || "—"}</td>
            </tr>
          ))}
          {total === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#8ba397]">Sin movimientos en el período</td></tr>}
        </tbody>
      </table>
      <Pager page={page} setPage={setPage} pages={pages} total={total} />
    </div>
  );
}

function DevolucionesTabs({ data, verMontos, money, num }) {
  return (
    <Tabs defaultValue="cli" className="w-full mt-4">
      <TabsList className="bg-white border border-[#d1dbd6]">
        <TabsTrigger value="cli" data-testid="dev-tab-cli">Devoluciones de clientes ({data.devoluciones_cliente?.length || 0})</TabsTrigger>
        <TabsTrigger value="prov" data-testid="dev-tab-prov">Devoluciones a proveedores ({data.devoluciones_proveedor?.length || 0})</TabsTrigger>
      </TabsList>
      <TabsContent value="cli">
        <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden mt-3" data-testid="dev-cli-table">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Fecha</th><th>NCC</th><th>Cliente</th><th className="text-right">Cant.</th>
                {verMontos && <th className="text-right">Subtotal</th>}
                <th>Lote</th><th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {(data.devoluciones_cliente || []).map((d, i) => (
                <tr key={i}>
                  <td className="tabular-nums">{d.fecha}</td>
                  <td className="font-mono text-xs">{d.numero}</td>
                  <td className="font-medium">{d.cliente_nombre || "—"}</td>
                  <td className="text-right tabular-nums">{num(d.cantidad)}</td>
                  {verMontos && <td className="text-right tabular-nums">{money(d.subtotal)}</td>}
                  <td className="font-mono text-xs">{d.lote || "—"}</td>
                  <td className="text-[#4a5c53] text-xs">{d.motivo || "—"}</td>
                </tr>
              ))}
              {(!data.devoluciones_cliente || data.devoluciones_cliente.length === 0) && (
                <tr><td colSpan={verMontos ? 7 : 6} className="text-center py-8 text-[#8ba397]">Sin devoluciones de clientes</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </TabsContent>
      <TabsContent value="prov">
        <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden mt-3" data-testid="dev-prov-table">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Fecha</th><th>NCP</th><th>Proveedor</th><th>F. compra</th><th className="text-right">Cant.</th><th>Lote</th><th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {(data.devoluciones_proveedor || []).map((d, i) => (
                <tr key={i}>
                  <td className="tabular-nums">{d.fecha}</td>
                  <td className="font-mono text-xs">{d.numero}</td>
                  <td className="font-medium">{d.proveedor_nombre || "—"}</td>
                  <td className="font-mono text-xs">{d.factura_compra_numero || "—"}</td>
                  <td className="text-right tabular-nums">{num(d.cantidad)}</td>
                  <td className="font-mono text-xs">{d.lote || "—"}</td>
                  <td className="text-[#4a5c53] text-xs">{d.motivo || "—"}</td>
                </tr>
              ))}
              {(!data.devoluciones_proveedor || data.devoluciones_proveedor.length === 0) && (
                <tr><td colSpan={7} className="text-center py-8 text-[#8ba397]">Sin devoluciones a proveedores</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </TabsContent>
    </Tabs>
  );
}

// Suppress unused import lint warnings for icon set
// eslint-disable-next-line no-unused-vars
const _icons = { CalIcon };
